import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';

import type {
  ApiConfigResponse,
  BaseApiConfig,
} from 'api/resources/apiConfig/types';
import { useGetApiConfigQueryKey } from 'hooks/api/apiConfig/hooks';
import { useToast } from 'hooks/useToast';

interface UseApiConfigPatchOptions {
  options?: Parameters<
    typeof useMutation<ApiConfigResponse, Error, BaseApiConfig[]>
  >[1];
}

export const useApiConfigPatch = ({
  options,
}: UseApiConfigPatchOptions = {}) => {
  const api = useApi();
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = useGetApiConfigQueryKey(accountId);

  return useMutation<ApiConfigResponse, Error, BaseApiConfig[]>(
    payload => api.methods.patchApiConfig(accountId, payload),
    {
      retry: false,
      onSuccess: data => {
        queryClient.setQueryData<ApiConfigResponse>(queryKey, data);
        toast.success({ title: 'API config updated' });
      },

      onError: () => {
        toast.error({
          title: 'There was an error updating the API config',
        });
      },
      ...options,
    },
  );
};

export default useApiConfigPatch;
